import { useReactiveVar } from '@apollo/client';
import qs from 'query-string';
import { useLocation } from '@react-router';
import { SORT_BY_CODE, activeFiltersVar } from '../../Catalog.cache';
import { useCallback } from 'react';
import { PRODUCT_FILTER_QS_PARSE_OPTIONS } from '../../Catalog.constants';
import { DeriveHrefArgs } from '../../components/RefinementFacet/RefinementFacet.types';
import { ProductCatalogAttributeSelectionType } from '@graphql/generated';
import { BUCKETED_CUSTOM_CODE } from '../../components/RefinementFacet/components/RefinementFacetPriceBuckets/RefinementFacetPriceBuckets.controller';

type UseFormattedRefinementFacetHrefOptions = {
  baseUrl?: string;
};

type UseFormattedRefinementFacetHrefReturn = {
  /**
   * Generates a new url where the provided composite code is toggled.
   *
   * @example
   * // current url: https://withjoy.com/shop?pf=brand%3Djabra%7Csony
   * deriveHref('brand:jabra'); // https://withjoy.com/shop?pf=brand%3Dsony
   */
  deriveHref: (args: DeriveHrefArgs) => string;
  removeFilterFromHref: (filterName: string) => string;
  resetFiltersHref: () => string;
};

/**
 * Returns a function that takes a filter attribute's composite code and returns a href where the filter is toggled on or off.
 */
export const useFormattedRefinementFacetHref = (options: UseFormattedRefinementFacetHrefOptions = {}): UseFormattedRefinementFacetHrefReturn => {
  // CAREFUL! activeFilters is mutable
  const activeFilters = useReactiveVar(activeFiltersVar);
  const location = useLocation();

  const baseUrl = options.baseUrl || `${location.pathname}${location.search}${location.hash}`;

  const getQueryStringFromParams = useCallback(
    queryParams => {
      const filterAsQueryParam = qs.stringify(queryParams, PRODUCT_FILTER_QS_PARSE_OPTIONS);

      return qs.stringifyUrl(
        {
          url: baseUrl,
          query: {
            pf: filterAsQueryParam,
          },
        },
        { skipNull: true, skipEmptyString: true },
      );
    },
    [baseUrl],
  );

  const deriveHref = useCallback(
    (filterArgs: DeriveHrefArgs) => {
      const { compositeCode: selectedCompositeCode, value: selectedValue } = filterArgs;
      const clonedFilters = { ...activeFilters };
      const currentValue = clonedFilters[selectedCompositeCode];

      /**
       * If the current value is a string, it's considered as active.
       * Otherwise, it's considered as a toggle so the value is inverted.
       */
      clonedFilters[selectedCompositeCode] = typeof currentValue === 'string' ? true : !currentValue;

      const queryParams = Object.keys(clonedFilters)
        .sort()
        .reduce((acc, filterCompositeCode) => {
          const isActive = clonedFilters[filterCompositeCode];
          if (isActive) {
            switch (filterCompositeCode) {
              case ProductCatalogAttributeSelectionType.priceRange:
              case SORT_BY_CODE:
                /**
                 * If a price bucket was selected, then remove priceRange from href
                 */
                if (filterCompositeCode === ProductCatalogAttributeSelectionType.priceRange && selectedCompositeCode.startsWith(ProductCatalogAttributeSelectionType.priceBucket)) {
                  delete acc[filterCompositeCode];
                  return acc;
                }

                /**
                 * If filterCompositeCode is matches with the selected one, then use the provided element value.
                 * Otherwise, use the value from the active filters.
                 */
                const thisValue = selectedCompositeCode === filterCompositeCode ? selectedValue : clonedFilters[filterCompositeCode];

                /**
                 * If the value is a valid range string or sortBy code then add it to the query params.
                 * Otherwise, remove it from the query params.
                 */
                if (thisValue && typeof thisValue === 'string' && thisValue !== '-') {
                  acc[filterCompositeCode] = [thisValue];
                } else {
                  delete acc[filterCompositeCode];
                }
                return acc;

              default:
                /**
                 * If priceRange or 'Custom' bucket was selected, remove all price buckets from href
                 * and leave only priceBucket:custom
                 */
                if (
                  [ProductCatalogAttributeSelectionType.priceRange, BUCKETED_CUSTOM_CODE].includes(selectedCompositeCode) &&
                  filterCompositeCode.startsWith(ProductCatalogAttributeSelectionType.priceBucket) &&
                  filterCompositeCode !== BUCKETED_CUSTOM_CODE
                ) {
                  delete acc[filterCompositeCode];
                  return acc;
                }

                /**
                 * If another priceBucket than 'Custom' was selected, then remove 'Custom' bucket from href
                 */
                if (
                  selectedCompositeCode.startsWith(ProductCatalogAttributeSelectionType.priceBucket) &&
                  selectedCompositeCode !== BUCKETED_CUSTOM_CODE &&
                  filterCompositeCode === BUCKETED_CUSTOM_CODE
                ) {
                  delete acc[filterCompositeCode];
                  return acc;
                }

                /**
                 * If it's not priceRange or sortBy, then get the value from splitting the composite code
                 */
                const [groupCode, valueCode] = filterCompositeCode.split(':');
                acc[groupCode] = acc[groupCode] || [];
                acc[groupCode].push(valueCode);
                return acc;
            }
          }

          return acc;
        }, {} as { [facet: string]: string[] });

      /**
       * If priceRange was selected, then ensure 'Custom' bucket is in queryParams
       */
      if (selectedCompositeCode === ProductCatalogAttributeSelectionType.priceRange) {
        queryParams[ProductCatalogAttributeSelectionType.priceBucket] = ['custom'];
      }

      return getQueryStringFromParams(queryParams);
    },
    [activeFilters, getQueryStringFromParams],
  );

  const removeFilterFromHref = useCallback(
    (filterName: string) => {
      const cloned = { ...activeFilters };
      const queryParams = Object.keys(cloned)
        .sort()
        .reduce((acc, compositeCode) => {
          const currentValue = cloned[compositeCode];
          /**
           * If the current value is a string or true, it's considered as active.
           */
          const isActive = typeof currentValue === 'string' ? true : currentValue;
          const [groupCode, valueCode] = compositeCode.split(':');

          if (isActive && groupCode !== filterName) {
            switch (compositeCode) {
              case ProductCatalogAttributeSelectionType.priceRange:
                /**
                 * If it's priceRange use the current value
                 */
                if (typeof currentValue === 'string') {
                  acc[compositeCode] = [currentValue];
                }
                return acc;

              default:
                /**
                 * If it's not priceRange, then get the value from splitting the composite code
                 */
                acc[groupCode] = acc[groupCode] || [];
                acc[groupCode].push(valueCode);
                return acc;
            }
          }
          return acc;
        }, {} as { [facet: string]: string[] });
      return getQueryStringFromParams(queryParams);
    },
    [activeFilters, getQueryStringFromParams],
  );

  const resetFiltersHref = useCallback(() => {
    const queryParams = {};
    return getQueryStringFromParams(queryParams);
  }, [getQueryStringFromParams]);

  /**
   * yo
   */
  return { deriveHref, resetFiltersHref, removeFilterFromHref };
};
